import { useState } from 'react';

import { FormState, validateForm } from './utils';

export const useForm = <T extends Partial<FormState>>(initialState: T) => {
  const [form, setForm] = useState<T>(initialState);
  const [errors, setErrors] = useState<
    Record<keyof T, string | number | undefined>
  >({} as Record<keyof T, string | number | undefined>);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setForm(prev => ({
      ...prev,
      [id]: value,
    }));
    setErrors(errors => ({
      ...errors,
      [id]: undefined,
    }));
  };

  const validateFormFn = (includeKeys?: Array<keyof T>) => {
    const validation = validateForm(form);
    const filteredValidation = includeKeys
      ? (Object.keys(validation) as Array<keyof FormState>).reduce(
          (acc, key) => ({
            ...acc,
            ...(includeKeys.includes(key as keyof T)
              ? { [key]: validation[key] }
              : {}),
          }),
          {} as Record<keyof T, string | undefined>,
        )
      : validation;
    if (Object.values(filteredValidation).length > 0) {
      setErrors(errors => ({
        ...errors,
        ...validation,
      }));
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm(prev => {
      const newForm = Object.keys(prev).reduce(
        (acc, key) => ({
          ...acc,
          [key]: '',
        }),
        {} as T,
      );

      return newForm;
    });

    setErrors(prev => {
      const newErrors = Object.keys(prev).reduce(
        (acc, key) => ({
          ...acc,
          [key]: undefined,
        }),
        {} as Record<keyof T, string | undefined>,
      );
      return newErrors;
    });
  };

  return {
    form,
    errors,
    onChange,
    validateForm: validateFormFn,
    setForm,
    setErrors,
    resetForm,
  };
};
